/**
 * Configuration Service
 * Handles environment-specific configuration
 * 
 * Updated: Now exports API_BASE_URL for all service files to use
 */

// 📊 Centralized logging
import { logger } from '../utils/logger';

// Create service-specific logger
const configLogger = logger.createServiceLogger('ConfigService');

interface AppConfig {
  cognito: {
    region: string;
    userPoolId: string;
    userPoolClientId: string;
  };
  api: {
    baseUrl: string;
    region: string;
  };
  s3: {
    photoBucket: string;
    region: string;
  };
  environment: 'development' | 'staging' | 'production';
}

class ConfigService {
  private config: AppConfig | null = null;

  /**
   * Initialize configuration based on environment
   */
  async initialize(): Promise<AppConfig> {
    if (this.config) {
      return this.config;
    }

    // Determine environment
    const environment = this.getEnvironment();
    
    // Load configuration based on environment
    this.config = await this.loadConfig(environment);
    
    configLogger.info('ConfigService initialized', { 
      environment,
      apiBaseUrl: this.config.api.baseUrl,
      cognitoUserPoolId: this.config.cognito.userPoolId
    });
    
    return this.config;
  }

  /**
   * Get current configuration
   */
  getConfig(): AppConfig {
    if (!this.config) {
      throw new Error('ConfigService not initialized. Call initialize() first.');
    }
    return this.config;
  }

  /**
   * Get API base URL (convenience method for service files)
   */
  getApiBaseUrl(): string {
    if (!this.config) {
      // Fallback to environment variable if not initialized
      const url = process.env.REACT_APP_API_BASE_URL;
      if (!url) {
        throw new Error('ConfigService not initialized and REACT_APP_API_BASE_URL not set');
      }
      return url;
    }
    return this.config.api.baseUrl;
  }

  /**
   * Determine current environment
   */
  private getEnvironment(): 'development' | 'staging' | 'production' {
    // Check for explicit environment variable
    const customEnv = process.env.REACT_APP_ENVIRONMENT;
    
    if (customEnv) {
      return customEnv as 'development' | 'staging' | 'production';
    }
    
    // Fallback to NODE_ENV
    const nodeEnv = process.env.NODE_ENV;
    
    if (nodeEnv === 'production') {
      return 'production';
    }
    
    return 'development'; // Default fallback
  }

  /**
   * Load configuration for specific environment
   */
  private async loadConfig(environment: string): Promise<AppConfig> {
    // All configuration comes from environment variables
    // The .env.development or .env.production files are loaded by Create React App
    
    const config: AppConfig = {
      cognito: {
        region: process.env.REACT_APP_COGNITO_REGION || 'us-west-2',
        userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || '',
        userPoolClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '',
      },
      api: {
        baseUrl: process.env.REACT_APP_API_BASE_URL || '',
        region: process.env.REACT_APP_AWS_REGION || 'us-west-2',
      },
      s3: {
        photoBucket: process.env.REACT_APP_S3_PHOTO_BUCKET || '',
        region: process.env.REACT_APP_AWS_REGION || 'us-west-2',
      },
      environment: environment as 'development' | 'staging' | 'production'
    };

    configLogger.debug('Configuration loaded', {
      environment,
      hasApiUrl: !!config.api.baseUrl,
      hasCognitoPool: !!config.cognito.userPoolId,
      hasS3Bucket: !!config.s3.photoBucket
    });

    return config;
  }

  /**
   * Validate configuration on startup
   */
  validateConfig(config: AppConfig): boolean {
    const required = [
      { name: 'API Base URL', value: config.api.baseUrl },
      { name: 'Cognito User Pool ID', value: config.cognito.userPoolId },
      { name: 'Cognito Client ID', value: config.cognito.userPoolClientId },
      { name: 'S3 Photo Bucket', value: config.s3.photoBucket }
    ];

    const missing = required.filter(item => !item.value || item.value.trim() === '');
    
    if (missing.length > 0) {
      configLogger.error('Missing required configuration values', { 
        missing: missing.map(m => m.name)
      });
      return false;
    }

    configLogger.info('Configuration validation passed');
    return true;
  }
}

// Export singleton instance
export const configService = new ConfigService();

// Export types for use in other files
export type { AppConfig };

// Export a constant that can be used in service files
// This will be populated after configService.initialize() is called in App.tsx
export const getApiBaseUrl = (): string => {
  return process.env.REACT_APP_API_BASE_URL || '';
};
